import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Calendar, Download, Users, Package, Tag, Clock, Filter, X, ScanLine, MapPin, User, FileBarChart, Edit, Check, Warehouse, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const GoodsTransferReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('staff-productivity');
  
  // Filters
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Data states
  const [staffProductivityData, setStaffProductivityData] = useState({ records: [], aggregates: {}, total: 0 });
  const [designWiseData, setDesignWiseData] = useState({ records: [], aggregates: {}, total: 0 });
  const [pendingData, setPendingData] = useState({ records: [], total: 0 });
  
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 50;
  
  // USN Popup
  const [showUsnPopup, setShowUsnPopup] = useState(false);
  const [usnPopupData, setUsnPopupData] = useState({ bundle_barcode: '', usns: [], count: 0 });
  const [isLoadingUsns, setIsLoadingUsns] = useState(false);
  
  // USN Tracking
  const [usnTrackingCode, setUsnTrackingCode] = useState('');
  const [usnTrackingData, setUsnTrackingData] = useState(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const usnInputRef = useRef(null);

  // Edit FG Assignment Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  
  // Item Master dropdowns for edit
  const [categories, setCategories] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [items, setItems] = useState([]);
  
  // Edit form state (FG Assignment)
  const [editForm, setEditForm] = useState({
    category4: '',
    design_name: '',
    shade: '',
    size: '',
    item_barcode: '',
    description: ''
  });

  // Edit Packing Modal
  const [showEditPackingModal, setShowEditPackingModal] = useState(false);
  const [editingPackingRecord, setEditingPackingRecord] = useState(null);
  const [newBundleBarcode, setNewBundleBarcode] = useState('');
  const [isSubmittingPackingEdit, setIsSubmittingPackingEdit] = useState(false);

  // Location Wise Stock Report
  const [locationStockData, setLocationStockData] = useState({ records: [], aggregates: {}, total: 0 });
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationSearchOpen, setLocationSearchOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');

  // Operation Type Filter for Design Wise tab
  const [selectedOperationType, setSelectedOperationType] = useState('all');

  useEffect(() => {
    fetchData();
  }, [activeTab, page, selectedOperationType]);

  // Fetch available locations when location-stock tab is selected
  useEffect(() => {
    if (activeTab === 'location-stock') {
      fetchAvailableLocations();
    }
  }, [activeTab]);

  // Refetch location stock data when selectedLocation changes
  useEffect(() => {
    if (activeTab === 'location-stock') {
      setPage(0);
      fetchData();
    }
  }, [selectedLocation]);

  const fetchAvailableLocations = async () => {
    try {
      const response = await apiClient.get('/goods-transfer/reports/locations');
      setAvailableLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      if (activeTab === 'staff-productivity') {
        const response = await apiClient.get(`/goods-transfer/reports/staff-productivity?${params}`);
        setStaffProductivityData(response.data);
      } else if (activeTab === 'design-wise') {
        // Add operation type filter for design wise
        if (selectedOperationType && selectedOperationType !== 'all') {
          params.append('operation_type', selectedOperationType);
        }
        const response = await apiClient.get(`/goods-transfer/reports/design-wise?${params}`);
        setDesignWiseData(response.data);
      } else if (activeTab === 'pending') {
        const response = await apiClient.get(`/goods-transfer/reports/pending-fg-assignment?${params}`);
        setPendingData(response.data);
      } else if (activeTab === 'location-stock') {
        const locationParams = new URLSearchParams();
        locationParams.append('skip', page * limit);
        locationParams.append('limit', limit);
        if (selectedLocation) locationParams.append('location_id', selectedLocation);
        if (search) locationParams.append('search', search);
        const response = await apiClient.get(`/goods-transfer/reports/location-wise-stock?${locationParams}`);
        setLocationStockData(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const clearFilters = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setSelectedLocation('');
    setLocationSearchQuery('');
    setSelectedOperationType('all');
    setPage(0);
    setTimeout(fetchData, 100);
  };

  const handleViewUsns = async (packingId, reportType) => {
    setIsLoadingUsns(true);
    setShowUsnPopup(true);
    try {
      const endpoint = reportType === 'staff-productivity' 
        ? `/goods-transfer/reports/staff-productivity/${packingId}/usns`
        : `/goods-transfer/reports/design-wise/${packingId}/usns`;
      const response = await apiClient.get(endpoint);
      setUsnPopupData(response.data);
    } catch (error) {
      console.error('Error fetching USNs:', error);
      toast.error('Failed to load USN details');
      setShowUsnPopup(false);
    } finally {
      setIsLoadingUsns(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      let endpoint = '';
      if (activeTab === 'staff-productivity') {
        endpoint = `/goods-transfer/reports/export/staff-productivity?${params}`;
      } else if (activeTab === 'design-wise') {
        // Include operation type filter in export
        if (selectedOperationType && selectedOperationType !== 'all') {
          params.append('operation_type', selectedOperationType);
        }
        endpoint = `/goods-transfer/reports/export/design-wise?${params}`;
      } else if (activeTab === 'location-stock') {
        const locationParams = new URLSearchParams();
        if (selectedLocation) locationParams.append('location_id', selectedLocation);
        if (search) locationParams.append('search', search);
        endpoint = `/goods-transfer/reports/export/location-wise-stock?${locationParams}`;
      } else {
        endpoint = `/goods-transfer/reports/export/pending-fg-assignment?${params}`;
      }

      const response = await apiClient.get(endpoint);
      const { data, filename } = response.data;

      if (!data || data.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Convert to CSV
      const headers = Object.keys(data[0]);
      const rows = data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','));
      const csvContent = [headers.join(','), ...rows].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const handleFetchUsnTracking = async () => {
    const code = usnTrackingCode.trim();
    if (!code) {
      toast.error('Please enter or scan a USN code');
      usnInputRef.current?.focus();
      return;
    }

    setIsLoadingTracking(true);
    setUsnTrackingData(null);
    try {
      const response = await apiClient.get(`/goods-transfer/reports/usn-tracking/${encodeURIComponent(code)}`);
      setUsnTrackingData(response.data);
      if (!response.data.found) {
        toast.error(response.data.message || 'USN not found');
      }
    } catch (error) {
      console.error('Error fetching USN tracking:', error);
      toast.error('Failed to fetch USN details');
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const handleClearTracking = () => {
    setUsnTrackingCode('');
    setUsnTrackingData(null);
    setTimeout(() => usnInputRef.current?.focus(), 100);
  };

  // Edit FG Assignment Functions
  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      category4: '',
      design_name: record.design_name || '',
      shade: record.shade || '',
      size: record.size || '',
      item_barcode: record.item_barcode || '',
      description: ''
    });
    setShowEditModal(true);
    fetchCategories();
  };

  // Edit Packing (Bundle Number) Functions
  const handleEditPackingClick = (record) => {
    setEditingPackingRecord(record);
    setNewBundleBarcode(record.bundle_barcode || '');
    setShowEditPackingModal(true);
  };

  const handleSubmitPackingEdit = async () => {
    if (!newBundleBarcode.trim()) {
      toast.error('Please enter a bundle barcode');
      return;
    }

    if (newBundleBarcode.trim().length < 5) {
      toast.error('Bundle barcode must be at least 5 characters');
      return;
    }

    setIsSubmittingPackingEdit(true);
    try {
      await apiClient.put('/goods-transfer/reports/staff-productivity/edit', {
        packing_id: editingPackingRecord.packing_id,
        new_bundle_barcode: newBundleBarcode.trim()
      });
      
      toast.success('Bundle number updated successfully');
      setShowEditPackingModal(false);
      setEditingPackingRecord(null);
      setNewBundleBarcode('');
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Error updating packing:', error);
      toast.error(error.response?.data?.detail || 'Failed to update bundle number');
    } finally {
      setIsSubmittingPackingEdit(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/goods-transfer/item-master/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDesigns = async (category4) => {
    try {
      const params = category4 ? `?category4=${encodeURIComponent(category4)}` : '';
      const response = await apiClient.get(`/goods-transfer/item-master/designs${params}`);
      const designList = response.data.designs || [];
      setDesigns(designList);
      
      // Auto-select if only one
      if (designList.length === 1) {
        setEditForm(prev => ({ ...prev, design_name: designList[0] }));
        fetchShades(category4, designList[0]);
      }
    } catch (error) {
      console.error('Error fetching designs:', error);
    }
  };

  const fetchShades = async (category4, designName) => {
    try {
      const params = new URLSearchParams();
      if (category4) params.append('category4', category4);
      params.append('design_name', designName);
      
      const response = await apiClient.get(`/goods-transfer/item-master/shades?${params.toString()}`);
      const shadeList = response.data.shades || [];
      setShades(shadeList);
      
      if (shadeList.length === 1) {
        setEditForm(prev => ({ ...prev, shade: shadeList[0] }));
        fetchSizes(category4, designName, shadeList[0]);
      }
    } catch (error) {
      console.error('Error fetching shades:', error);
    }
  };

  const fetchSizes = async (category4, designName, shade) => {
    try {
      const params = new URLSearchParams();
      if (category4) params.append('category4', category4);
      params.append('design_name', designName);
      params.append('shade', shade);
      
      const response = await apiClient.get(`/goods-transfer/item-master/sizes?${params.toString()}`);
      const sizeList = response.data.sizes || [];
      setSizes(sizeList);
      
      if (sizeList.length === 1) {
        setEditForm(prev => ({ ...prev, size: sizeList[0] }));
        fetchItems(category4, designName, shade, sizeList[0]);
      }
    } catch (error) {
      console.error('Error fetching sizes:', error);
    }
  };

  const fetchItems = async (category4, designName, shade, size) => {
    try {
      const params = new URLSearchParams();
      if (category4) params.append('category4', category4);
      params.append('design_name', designName);
      params.append('shade', shade);
      params.append('size', size);
      
      const response = await apiClient.get(`/goods-transfer/item-master/lookup?${params.toString()}`);
      const itemList = response.data.items || [];
      setItems(itemList);
      
      if (itemList.length === 1) {
        setEditForm(prev => ({
          ...prev,
          item_barcode: itemList[0].item_barcode,
          description: itemList[0].description || `${designName} ${shade} ${size}`
        }));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const handleCategoryChange = (value) => {
    setEditForm(prev => ({ ...prev, category4: value, design_name: '', shade: '', size: '', item_barcode: '' }));
    setDesigns([]);
    setShades([]);
    setSizes([]);
    setItems([]);
    fetchDesigns(value);
  };

  const handleDesignChange = (value) => {
    setEditForm(prev => ({ ...prev, design_name: value, shade: '', size: '', item_barcode: '' }));
    setShades([]);
    setSizes([]);
    setItems([]);
    fetchShades(editForm.category4, value);
  };

  const handleShadeChange = (value) => {
    setEditForm(prev => ({ ...prev, shade: value, size: '', item_barcode: '' }));
    setSizes([]);
    setItems([]);
    fetchSizes(editForm.category4, editForm.design_name, value);
  };

  const handleSizeChange = (value) => {
    setEditForm(prev => ({ ...prev, size: value, item_barcode: '' }));
    setItems([]);
    fetchItems(editForm.category4, editForm.design_name, editForm.shade, value);
  };

  const handleItemChange = (value) => {
    const selectedItem = items.find(i => i.item_barcode === value);
    setEditForm(prev => ({
      ...prev,
      item_barcode: value,
      description: selectedItem?.description || `${editForm.design_name} ${editForm.shade} ${editForm.size}`
    }));
  };

  const handleSubmitEdit = async () => {
    if (!editForm.design_name || !editForm.item_barcode || !editForm.shade || !editForm.size) {
      toast.error('Please select all required fields');
      return;
    }

    setIsSubmittingEdit(true);
    try {
      await apiClient.put('/goods-transfer/reports/design-wise/edit', {
        assignment_id: editingRecord.assignment_id,
        bundle_barcode: editingRecord.bundle_barcode,
        new_item_barcode: editForm.item_barcode,
        new_design_name: editForm.design_name,
        new_shade: editForm.shade,
        new_size: editForm.size,
        new_description: editForm.description
      });
      
      toast.success('FG Assignment updated successfully');
      setShowEditModal(false);
      setEditingRecord(null);
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Error updating FG assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to update FG Assignment');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const renderAggregates = (aggregates) => (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-blue-700">No of Staffs</p>
            <p className="text-2xl font-bold text-blue-900">{aggregates.staff_count || 0}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-green-700">No of Bundles</p>
            <p className="text-2xl font-bold text-green-900">{aggregates.bundle_count || 0}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
            <Tag className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-purple-700">No of USNs</p>
            <p className="text-2xl font-bold text-purple-900">{aggregates.usn_count || 0}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLocationStockAggregates = (aggregates) => (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-teal-700">No of Designs</p>
            <p className="text-2xl font-bold text-teal-900" data-testid="location-stock-design-count">{aggregates.design_count || 0}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-green-700">No of Bundles</p>
            <p className="text-2xl font-bold text-green-900" data-testid="location-stock-bundle-count">{aggregates.bundle_count || 0}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
            <Tag className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-purple-700">Count of USNs</p>
            <p className="text-2xl font-bold text-purple-900" data-testid="location-stock-usn-count">{aggregates.usn_count || 0}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLocationStockFilters = () => {
    // Filter locations based on search query
    const filteredLocations = availableLocations.filter(loc =>
      loc.toLowerCase().includes(locationSearchQuery.toLowerCase())
    );

    return (
      <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-4 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Location Dropdown with Search */}
          <div className="w-[280px]">
            <label className="text-sm text-[#5A5F6E] mb-1 block">Select Location</label>
            <Popover open={locationSearchOpen} onOpenChange={setLocationSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={locationSearchOpen}
                  className="w-full justify-between font-normal"
                  data-testid="location-dropdown-btn"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#8A8F9E]" />
                    {selectedLocation ? selectedLocation : "All Locations"}
                  </div>
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search location..."
                    value={locationSearchQuery}
                    onValueChange={setLocationSearchQuery}
                    data-testid="location-search-input"
                  />
                  <CommandList>
                    <CommandEmpty>No location found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value=""
                        onSelect={() => {
                          setSelectedLocation('');
                          setLocationSearchOpen(false);
                          setLocationSearchQuery('');
                        }}
                        data-testid="location-option-all"
                      >
                        <Warehouse className="mr-2 h-4 w-4" />
                        All Locations
                        {!selectedLocation && <Check className="ml-auto h-4 w-4" />}
                      </CommandItem>
                      {filteredLocations.map((location) => (
                        <CommandItem
                          key={location}
                          value={location}
                          onSelect={() => {
                            setSelectedLocation(location);
                            setLocationSearchOpen(false);
                            setLocationSearchQuery('');
                          }}
                          data-testid={`location-option-${location}`}
                        >
                          <MapPin className="mr-2 h-4 w-4" />
                          {location}
                          {selectedLocation === location && <Check className="ml-auto h-4 w-4" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Universal Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm text-[#5A5F6E] mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Location, Barcode, Design Name..."
                className="pl-10"
                data-testid="location-stock-search-input"
              />
            </div>
          </div>

          <Button onClick={handleSearch} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="location-stock-search-btn">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>

          <Button variant="outline" onClick={clearFilters} data-testid="location-stock-clear-btn">
            <Filter className="w-4 h-4 mr-2" />
            Clear
          </Button>

          <Button variant="outline" onClick={handleExport} className="border-green-500 text-green-600 hover:bg-green-50" data-testid="location-stock-export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-4 shadow-sm">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm text-[#5A5F6E] mb-1 block">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Staff ID, Staff Name, Bundle No, Design..."
              className="pl-10"
            />
          </div>
        </div>

        {activeTab !== 'pending' && (
          <>
            <div className="w-[150px]">
              <label className="text-sm text-[#5A5F6E] mb-1 block">From Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="w-[150px]">
              <label className="text-sm text-[#5A5F6E] mb-1 block">To Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </>
        )}

        {activeTab === 'design-wise' && (
          <div className="w-[180px]">
            <label className="text-sm text-[#5A5F6E] mb-1 block">Operation Type</label>
            <select
              value={selectedOperationType}
              onChange={(e) => { setSelectedOperationType(e.target.value); setPage(0); }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="all">All Operations</option>
              <option value="Main Label Printing">Main Label Printing</option>
              <option value="FG Assignment">FG Assignment</option>
              <option value="Packing">Packing</option>
            </select>
          </div>
        )}

        <Button onClick={handleSearch} className="bg-[#2D5F4F] hover:bg-[#234A3D]">
          <Search className="w-4 h-4 mr-2" />
          Search
        </Button>

        <Button variant="outline" onClick={clearFilters}>
          <Filter className="w-4 h-4 mr-2" />
          Clear
        </Button>

        <Button variant="outline" onClick={handleExport} className="border-green-500 text-green-600 hover:bg-green-50">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>
    </div>
  );

  const renderPagination = (total) => (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0]">
      <p className="text-sm text-[#5A5F6E]">
        Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => p + 1)}
          disabled={(page + 1) * limit >= total}
        >
          Next
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Goods Transfer Reports</h1>
              <p className="text-sm text-[#E8DCC8]">Packing & FG Assignment Analytics</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); }}>
          <TabsList className="mb-4 bg-white border">
            <TabsTrigger value="staff-productivity" className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white">
              Staff Productivity
            </TabsTrigger>
            <TabsTrigger value="design-wise" className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white">
              Design Wise
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              Pending FG Assignment
              {pendingData.total > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingData.total}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="usn-tracking" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <ScanLine className="w-4 h-4 mr-1" />
              USN Tracking
            </TabsTrigger>
            <TabsTrigger value="location-stock" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="location-stock-tab">
              <Warehouse className="w-4 h-4 mr-1" />
              Location Stock
            </TabsTrigger>
          </TabsList>

          {/* Staff Productivity Report */}
          <TabsContent value="staff-productivity">
            {renderFilters()}
            {renderAggregates(staffProductivityData.aggregates || {})}
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : staffProductivityData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Bundle No</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {staffProductivityData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium">{record.staff_id}</td>
                            <td className="px-4 py-3">{record.staff_name || '-'}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.bundle_barcode}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewUsns(record.packing_id, 'staff-productivity')}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                              >
                                {record.usn_count}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{formatDate(record.timestamp)}</td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditPackingClick(record)}
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                data-testid={`edit-packing-${record.packing_id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(staffProductivityData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Design Wise Report */}
          <TabsContent value="design-wise">
            {renderFilters()}
            {renderAggregates(designWiseData.aggregates || {})}
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : designWiseData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Tag className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Bundle No</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Barcode</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Operation</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {designWiseData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium">{record.staff_id}</td>
                            <td className="px-4 py-3">{record.staff_name || '-'}</td>
                            <td className="px-4 py-3">{record.design_name}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.bundle_barcode}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.item_barcode}</td>
                            <td className="px-4 py-3 text-center">
                              {record.packing_id ? (
                                <button
                                  onClick={() => handleViewUsns(record.packing_id, 'design-wise')}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                                >
                                  {record.usn_count}
                                </button>
                              ) : (
                                <span className="font-semibold text-purple-600">{record.usn_count}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{formatDate(record.timestamp)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                record.operation_type === 'Main Label Printing' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : record.operation_type === 'FG Assignment'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {record.operation_type || 'FG Assignment'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {record.operation_type !== 'Main Label Printing' && record.operation_type !== 'Packing' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditClick(record)}
                                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                  data-testid={`edit-fg-${record.assignment_id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(designWiseData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Pending FG Assignment */}
          <TabsContent value="pending">
            {renderFilters()}
            
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 mb-4">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-orange-700">Pending Bundles</p>
                  <p className="text-2xl font-bold text-orange-900">{pendingData.total || 0}</p>
                </div>
              </CardContent>
            </Card>
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : pendingData.records?.length === 0 ? (
                <div className="p-12 text-center text-green-600">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="font-semibold">All bundles have been assigned!</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Bundle No</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID (Packing)</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">USN Count</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Packing Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {pendingData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-orange-50">
                            <td className="px-4 py-3 font-mono font-semibold text-orange-700">{record.bundle_barcode}</td>
                            <td className="px-4 py-3">{record.staff_id}</td>
                            <td className="px-4 py-3 text-center font-semibold">{record.usn_count}</td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{formatDate(record.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(pendingData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* USN Tracking */}
          <TabsContent value="usn-tracking">
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <ScanLine className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-[#1A1F2E]">Track USN Lifecycle</h2>
              </div>
              <p className="text-sm text-[#5A5F6E] mb-4">
                Enter or scan a USN code to view its complete journey through the Goods Transfer module.
              </p>
              
              <div className="flex gap-3 items-end">
                <div className="flex-1 max-w-md">
                  <label className="text-sm text-[#5A5F6E] mb-1 block">USN Code</label>
                  <div className="relative">
                    <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                    <Input
                      ref={usnInputRef}
                      value={usnTrackingCode}
                      onChange={(e) => setUsnTrackingCode(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleFetchUsnTracking()}
                      placeholder="Enter or scan USN code..."
                      className="pl-10 font-mono"
                      data-testid="usn-tracking-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleFetchUsnTracking} 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={isLoadingTracking}
                  data-testid="usn-tracking-fetch-btn"
                >
                  {isLoadingTracking ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Fetch Details
                    </>
                  )}
                </Button>
                {usnTrackingData && (
                  <Button 
                    variant="outline" 
                    onClick={handleClearTracking}
                    data-testid="usn-tracking-clear-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* USN Tracking Results */}
            {usnTrackingData && (
              <div className="space-y-4" data-testid="usn-tracking-results">
                {!usnTrackingData.found ? (
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-6 text-center">
                      <X className="w-12 h-12 mx-auto mb-3 text-red-400" />
                      <p className="text-red-700 font-medium">{usnTrackingData.message || 'USN not found'}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* USN Info Header */}
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                            <Tag className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-blue-700">USN Code</p>
                            <p className="text-xl font-bold font-mono text-blue-900" data-testid="usn-tracking-code-display">
                              {usnTrackingData.usn_code}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Design & Barcode Info */}
                      <Card className="border-[#E5DFD0]">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-[#2D5F4F]">
                            <FileBarChart className="w-5 h-5" />
                            <h3 className="font-semibold">Item Details</h3>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Design Name:</span>
                              <span className="font-medium" data-testid="usn-tracking-design">{usnTrackingData.design_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Item Barcode:</span>
                              <span className="font-mono font-medium" data-testid="usn-tracking-barcode">{usnTrackingData.item_barcode || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Bundle No:</span>
                              <span className="font-mono font-medium" data-testid="usn-tracking-bundle">{usnTrackingData.bundle_barcode || '-'}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Packing Details */}
                      <Card className={`border-[#E5DFD0] ${usnTrackingData.packing?.staff_id ? 'bg-green-50/50' : 'bg-gray-50'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-green-700">
                            <Package className="w-5 h-5" />
                            <h3 className="font-semibold">Operation 1: Packing</h3>
                            {usnTrackingData.packing?.staff_id && (
                              <span className="ml-auto text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Done</span>
                            )}
                          </div>
                          {usnTrackingData.packing?.staff_id ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Staff ID:</span>
                                <span className="font-medium flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {usnTrackingData.packing.staff_id}
                                </span>
                              </div>
                              {usnTrackingData.packing.staff_name && (
                                <div className="flex justify-between">
                                  <span className="text-[#5A5F6E]">Staff Name:</span>
                                  <span className="font-medium">{usnTrackingData.packing.staff_name}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Timestamp:</span>
                                <span className="text-xs">{formatDate(usnTrackingData.packing.timestamp)}</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Not yet packed</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* FG Assignment Details */}
                      <Card className={`border-[#E5DFD0] ${usnTrackingData.fg_assignment?.staff_id ? 'bg-purple-50/50' : 'bg-gray-50'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-purple-700">
                            <Tag className="w-5 h-5" />
                            <h3 className="font-semibold">Operation 2: FG Assignment</h3>
                            {usnTrackingData.fg_assignment?.staff_id && (
                              <span className="ml-auto text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full">Done</span>
                            )}
                          </div>
                          {usnTrackingData.fg_assignment?.staff_id ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Staff ID:</span>
                                <span className="font-medium flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {usnTrackingData.fg_assignment.staff_id}
                                </span>
                              </div>
                              {usnTrackingData.fg_assignment.staff_name && (
                                <div className="flex justify-between">
                                  <span className="text-[#5A5F6E]">Staff Name:</span>
                                  <span className="font-medium">{usnTrackingData.fg_assignment.staff_name}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Timestamp:</span>
                                <span className="text-xs">{formatDate(usnTrackingData.fg_assignment.timestamp)}</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Not yet assigned</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Location Details */}
                      <Card className={`border-[#E5DFD0] ${usnTrackingData.location?.location_id ? 'bg-orange-50/50' : 'bg-gray-50'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-orange-700">
                            <MapPin className="w-5 h-5" />
                            <h3 className="font-semibold">Operation 3: Location</h3>
                            {usnTrackingData.location?.location_id && (
                              <span className="ml-auto text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Done</span>
                            )}
                          </div>
                          {usnTrackingData.location?.location_id ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Location ID:</span>
                                <span className="font-mono font-medium">{usnTrackingData.location.location_id}</span>
                              </div>
                              {usnTrackingData.location.staff_id && (
                                <div className="flex justify-between">
                                  <span className="text-[#5A5F6E]">Staff ID:</span>
                                  <span className="font-medium flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {usnTrackingData.location.staff_id}
                                  </span>
                                </div>
                              )}
                              {usnTrackingData.location.staff_name && (
                                <div className="flex justify-between">
                                  <span className="text-[#5A5F6E]">Staff Name:</span>
                                  <span className="font-medium">{usnTrackingData.location.staff_name}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Timestamp:</span>
                                <span className="text-xs">{formatDate(usnTrackingData.location.timestamp)}</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">No location assigned</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Progress Timeline */}
                    <Card className="border-[#E5DFD0]">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-[#1A1F2E] mb-4">Progress Timeline</h3>
                        <div className="flex items-center justify-between">
                          {/* Packing Step */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              usnTrackingData.packing?.staff_id ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              <Package className="w-5 h-5" />
                            </div>
                            <span className="text-xs mt-2 text-center">Packing</span>
                          </div>

                          {/* Connector */}
                          <div className={`flex-1 h-1 mx-2 ${
                            usnTrackingData.packing?.staff_id ? 'bg-green-500' : 'bg-gray-200'
                          }`} />

                          {/* FG Assignment Step */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              usnTrackingData.fg_assignment?.staff_id ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              <Tag className="w-5 h-5" />
                            </div>
                            <span className="text-xs mt-2 text-center">FG Assignment</span>
                          </div>

                          {/* Connector */}
                          <div className={`flex-1 h-1 mx-2 ${
                            usnTrackingData.fg_assignment?.staff_id ? 'bg-purple-500' : 'bg-gray-200'
                          }`} />

                          {/* Location Step */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              usnTrackingData.location?.location_id ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              <MapPin className="w-5 h-5" />
                            </div>
                            <span className="text-xs mt-2 text-center">Location</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* Empty State */}
            {!usnTrackingData && !isLoadingTracking && (
              <Card className="bg-gray-50 border-dashed border-2 border-gray-200">
                <CardContent className="p-12 text-center">
                  <ScanLine className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-[#5A5F6E] mb-2">No USN searched yet</p>
                  <p className="text-sm text-gray-400">Enter a USN code above to track its complete lifecycle</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Location Wise Stock Report */}
          <TabsContent value="location-stock" data-testid="location-stock-content">
            {renderLocationStockFilters()}
            {renderLocationStockAggregates(locationStockData.aggregates || {})}
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : locationStockData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Warehouse className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No stock records found</p>
                  <p className="text-sm mt-2">No bundles have been assigned to locations yet</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="location-stock-table">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Location Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Barcode</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Number of Bundles</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {locationStockData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]" data-testid={`location-stock-row-${idx}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-teal-600" />
                                <span className="font-medium">{record.location_id}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-sm">{record.item_barcode}</td>
                            <td className="px-4 py-3">{record.design_name}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-semibold">
                                {record.bundle_count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-sm font-semibold">
                                {record.usn_count}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(locationStockData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* USN Popup */}
      <Dialog open={showUsnPopup} onOpenChange={setShowUsnPopup}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-500" />
              USN Details - {usnPopupData.bundle_barcode}
            </DialogTitle>
          </DialogHeader>
          
          {isLoadingUsns ? (
            <div className="p-8 text-center text-gray-500">Loading USNs...</div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">
                Total: <span className="font-semibold">{usnPopupData.count}</span> USNs
              </p>
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">USN Code</th>
                      <th className="px-3 py-2 text-left">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {usnPopupData.usns?.map((usn, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 font-mono">{usn.usn_code}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{formatDate(usn.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit FG Assignment Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-orange-600" />
              Edit FG Assignment
            </DialogTitle>
          </DialogHeader>
          
          {editingRecord && (
            <div className="space-y-6">
              {/* Current Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700 mb-2">Current Assignment</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Bundle:</span> <span className="font-mono">{editingRecord.bundle_barcode}</span></div>
                  <div><span className="text-gray-500">Design:</span> {editingRecord.design_name}</div>
                  <div><span className="text-gray-500">Barcode:</span> <span className="font-mono">{editingRecord.item_barcode}</span></div>
                  <div><span className="text-gray-500">USN Count:</span> {editingRecord.usn_count}</div>
                </div>
              </div>

              {/* Edit Form */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700">Select New Design</h4>
                
                {/* Category Filter */}
                <div>
                  <Label>Category 4 (Optional Filter)</Label>
                  <Select value={editForm.category4} onValueChange={handleCategoryChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category to filter..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Design Name */}
                <div>
                  <Label>Design Name *</Label>
                  <Select 
                    value={editForm.design_name} 
                    onValueChange={handleDesignChange}
                    disabled={designs.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={designs.length === 0 ? "Select category first or load designs" : "Select design..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {designs.map((design) => (
                        <SelectItem key={design} value={design}>{design}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {designs.length === 1 && (
                    <p className="text-xs text-green-600 mt-1">Auto-selected (only one option)</p>
                  )}
                </div>

                {/* Shade */}
                <div>
                  <Label>Shade *</Label>
                  <Select 
                    value={editForm.shade} 
                    onValueChange={handleShadeChange}
                    disabled={shades.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={!editForm.design_name ? "Select design first" : "Select shade..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {shades.map((shade) => (
                        <SelectItem key={shade} value={shade}>{shade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {shades.length === 1 && (
                    <p className="text-xs text-green-600 mt-1">Auto-selected (only one option)</p>
                  )}
                </div>

                {/* Size */}
                <div>
                  <Label>Size *</Label>
                  <Select 
                    value={editForm.size} 
                    onValueChange={handleSizeChange}
                    disabled={sizes.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={!editForm.shade ? "Select shade first" : "Select size..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {sizes.map((size) => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {sizes.length === 1 && (
                    <p className="text-xs text-green-600 mt-1">Auto-selected (only one option)</p>
                  )}
                </div>

                {/* Item Barcode */}
                <div>
                  <Label>Item Barcode *</Label>
                  <Select 
                    value={editForm.item_barcode} 
                    onValueChange={handleItemChange}
                    disabled={items.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={!editForm.size ? "Select size first" : "Select item..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((item) => (
                        <SelectItem key={item.item_barcode} value={item.item_barcode}>
                          {item.item_barcode} - {item.description || ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {items.length === 1 && (
                    <p className="text-xs text-green-600 mt-1">Auto-selected (only one option)</p>
                  )}
                </div>

                {/* Selected Item Summary */}
                {editForm.item_barcode && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-medium text-sm text-green-700 mb-2 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      New Assignment
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">Design:</span> {editForm.design_name}</div>
                      <div><span className="text-gray-500">Shade:</span> {editForm.shade}</div>
                      <div><span className="text-gray-500">Size:</span> {editForm.size}</div>
                      <div><span className="text-gray-500">Barcode:</span> <span className="font-mono">{editForm.item_barcode}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmittingEdit}
                >
                  Cancel
                </Button>
                <Button 
                  type="button"
                  onClick={handleSubmitEdit}
                  disabled={!editForm.item_barcode || isSubmittingEdit}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {isSubmittingEdit ? 'Updating...' : 'Update Assignment'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Packing (Bundle Number) Modal */}
      <Dialog open={showEditPackingModal} onOpenChange={setShowEditPackingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Bundle Number
            </DialogTitle>
          </DialogHeader>
          
          {editingPackingRecord && (
            <div className="space-y-6">
              {/* Current Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700 mb-2">Current Packing</h4>
                <div className="grid gap-2 text-sm">
                  <div><span className="text-gray-500">Staff ID:</span> <span className="font-medium">{editingPackingRecord.staff_id}</span></div>
                  <div><span className="text-gray-500">Current Bundle:</span> <span className="font-mono font-medium text-red-600">{editingPackingRecord.bundle_barcode}</span></div>
                  <div><span className="text-gray-500">USN Count:</span> {editingPackingRecord.usn_count}</div>
                  <div><span className="text-gray-500">Timestamp:</span> {formatDate(editingPackingRecord.timestamp)}</div>
                </div>
              </div>

              {/* Edit Form */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">New Bundle Number *</label>
                  <Input
                    value={newBundleBarcode}
                    onChange={(e) => setNewBundleBarcode(e.target.value)}
                    placeholder="Enter correct bundle barcode..."
                    className="font-mono"
                    data-testid="edit-bundle-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter the correct bundle barcode to replace the wrongly scanned one</p>
                </div>

                {newBundleBarcode.trim() && newBundleBarcode.trim() !== editingPackingRecord.bundle_barcode && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 text-sm">
                      <Check className="w-4 h-4" />
                      <span>Will change from <span className="font-mono font-medium">{editingPackingRecord.bundle_barcode}</span> to <span className="font-mono font-medium">{newBundleBarcode.trim()}</span></span>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowEditPackingModal(false);
                    setEditingPackingRecord(null);
                    setNewBundleBarcode('');
                  }}
                  disabled={isSubmittingPackingEdit}
                >
                  Cancel
                </Button>
                <Button 
                  type="button"
                  onClick={handleSubmitPackingEdit}
                  disabled={!newBundleBarcode.trim() || newBundleBarcode.trim() === editingPackingRecord.bundle_barcode || isSubmittingPackingEdit}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isSubmittingPackingEdit ? 'Updating...' : 'Update Bundle'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsTransferReports;
